import { useEffect, useMemo, useRef, useState } from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import { PlanningPeriodCollaboratorsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import useParticipants from 'modules/planning/hooks/useParticipants'

import type { SelectedParticipant } from './ParticipantRow'
import ParticipantSelector from './ParticipantSelector'

interface ManageCollaboratorsModalProps {
  periodId: string
  onClose: () => void
}

const ManageCollaboratorsModal = ({ periodId, onClose }: ManageCollaboratorsModalProps) => {
  const { company } = useCurrentSession()
  const [selectedParticipants, setSelectedParticipants] = useState<SelectedParticipant[]>([])
  const { save, loading } = useParticipants({ periodId, companyId: company.id })

  // Queries
  const { data } = useQuery(PlanningPeriodCollaboratorsDocument, {
    variables: { id: periodId },
  })

  const currentParticipants = useMemo(
    () => data?.planning?.period?.participantsConnection.edges.map((edge) => edge.node) ?? [],
    [data]
  )

  const initializedRef = useRef(false)

  useEffect(() => {
    if (initializedRef.current || currentParticipants.length === 0) return
    initializedRef.current = true

    setSelectedParticipants(
      currentParticipants.map((p) => ({
        employeeId: p.employee.id,
        role: p.role,
      }))
    )
  }, [currentParticipants])

  const handleSubmit = async () => {
    const result = await save(selectedParticipants, currentParticipants)

    if (result.success) {
      onClose()
    }
  }

  return (
    <F0Dialog
      title={i18n.t('planning.manage_collaborators.title')}
      isOpen
      onClose={onClose}
      primaryAction={{
        label: i18n.t('planning.manage_collaborators.save_button'),
        onClick: handleSubmit,
        loading,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <ParticipantSelector
        selectedParticipants={selectedParticipants}
        onParticipantsChange={setSelectedParticipants}
      />
    </F0Dialog>
  )
}

export default ManageCollaboratorsModal
